import React from 'react'
import { Stack } from 'react-bootstrap'
import type { DataLoadOptions, SelectOption } from '@ztechapps/webui.input'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { chatPermissions } from '@ztechapps/chat.common'
import { useConfirmDialog, useRole } from '@ztechapps/common.hook'
import { Field, FormModal } from '@ztechapps/webui.form'
import { ConversationSelect } from '../ConversationSelect'
import { buildConversationSearch } from '../ConversationSelect/buildConversationSearch'
import { conversationSelectOptions } from '../ConversationSelect/conversationSelectOptions'
import { attachableChatsQuery } from '../Inbox/graphql'
import { ConversationActionItem } from './ConversationActionItem'
import { useConversationContext } from './Context'
import { useLinkChatToDocument } from './useLinkChatToDocument'

type AttachConversationFormValues = { conversation?: SelectOption }

export type AttachConversationProps = {
  /** The document-side conversation id — set when the document already has a thread, which attaching replaces. */
  conversationId?: string
}

const validate = ({ conversation }: AttachConversationFormValues) =>
  conversation?._id ? {} : { conversation: 'Pick the conversation to attach.' }

const TriggerItem = ({ onClick, label }: Pick<ModalTriggerProps, 'onClick'> & { label: string }) => {
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })

  return (
    <ConversationActionItem
      icon="link"
      label={label}
      onClick={onClick}
      disabledReason={canEdit ? undefined : 'You need the Conversation edit permission to attach conversations.'}
    />
  )
}

// Module scope: a trigger component built per render is a new type each time and remounts the item.
const AttachItem = ({ onClick }: ModalTriggerProps) => <TriggerItem onClick={onClick} label="Attach conversation" />
const ReplaceItem = ({ onClick }: ModalTriggerProps) => <TriggerItem onClick={onClick} label="Replace conversation" />

// A menu item of the conversation actions dropdown that opens the picker. A document already linked to a
// thread asks before the pick replaces it.
export const AttachConversation = ({ conversationId }: AttachConversationProps) => {
  const hasConversation = !!conversationId
  const { documentKind, documentId, onConversationLinked } = useConversationContext()
  const [linkChatToDocument] = useLinkChatToDocument()
  // Only the record's customer's conversations, searched by the typed text.
  const dataLoadOptions = {
    query: attachableChatsQuery,
    resultFieldName: 'getAttachableChats',
    filter: ({ input }) => ({ ...buildConversationSearch(input), documentKind, documentId }),
    options: conversationSelectOptions,
    // A link changes the list, so a reopened dialog never reads a cached one.
    fetchPolicy: 'network-only',
  } as DataLoadOptions

  // The host re-reads the document so its `activeConversationId` shows the new thread. Awaited, so a failed
  // re-read keeps the picker open instead of looking like a success.
  const link = async ({ conversation }: AttachConversationFormValues) => {
    await linkChatToDocument({ chatId: conversation._id, documentKind, documentId })
    await onConversationLinked?.()
  }

  // Cancel resolves the submit without linking, so the form closes the picker and nothing changes.
  const { onConfirm } = useConfirmDialog({
    title: 'Replace the attached conversation?',
    text: 'The document keeps one conversation. The current one is unlinked from it and the picked one takes its place.',
    buttons: { confirm: { text: 'Replace conversation' } },
    confirmed: link,
  })

  return (
    <FormModal<AttachConversationFormValues>
      trigger={hasConversation ? ReplaceItem : AttachItem}
      title={hasConversation ? 'Replace conversation' : 'Attach conversation'}
      saveText={hasConversation ? 'Replace' : 'Attach'}
      saveCancelButtonsProps={{
        saveTitle: hasConversation ? 'Replace the attached conversation' : 'Attach the picked conversation',
        cancelTitle: 'Close without changing the attached conversation',
      }}
      initialValues={{}}
      validate={validate}
      onSubmit={(values) => (hasConversation ? onConfirm(values) : link(values))}
    >
      <Stack gap={2}>
        <Field
          name="conversation"
          label="Conversation"
          component={ConversationSelect}
          dataLoadOptions={dataLoadOptions}
          isClearable={false}
          onChange
        />
      </Stack>
    </FormModal>
  )
}
